import { useState, useRef, useCallback } from "react";
import { useUploadImage } from "../../../lib/api/images.api";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];

interface ImageUploaderProps {
  readonly taskId?: string;
  readonly eventId?: string;
}

export function ImageUploader({ taskId, eventId }: ImageUploaderProps) {
  const [pending, setPending] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync } = useUploadImage();

  const uploadFiles = useCallback(async (files: File[]) => {
    const valid = files.filter((f) => {
      if (!ACCEPTED_TYPES.includes(f.type)) { setError("Format non supporté"); return false; }
      if (f.size > MAX_FILE_SIZE) { setError("5 Mo max par image"); return false; }
      return true;
    });
    if (valid.length === 0) return;

    setError(null);
    setPending(valid.length);
    setTotal(valid.length);

    for (const file of valid) {
      try {
        await mutateAsync({ file, taskId, eventId });
      } catch {
        setError("Échec de l'upload");
      }
      setPending((n) => n - 1);
    }
    setTotal(0);
  }, [taskId, eventId, mutateAsync]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadFiles(Array.from(files));
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const isUploading = pending > 0;

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={isUploading}
        className="flex items-center gap-1 px-2 py-1 text-[11px] text-ink-faint hover:text-ink
          bg-surface border border-border/60 rounded-md hover:border-accent/40 transition-colors
          disabled:opacity-50 disabled:pointer-events-none"
      >
        {isUploading ? (
          <>
            <div className="w-3 h-3 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            {pending}/{total}
          </>
        ) : (
          <>
            <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            Image
          </>
        )}
      </button>
      {error && <span className="text-[10px] text-danger">{error}</span>}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}
